import { RoomContext } from "@livekit/components-react";
import {
	Columns2,
	LayoutDashboard,
	Radio,
	Rows2,
	Square,
	Video,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	ResizableHandle,
	ResizablePanel,
	ResizablePanelGroup,
} from "@/components/ui/resizable";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { BroadcastReactions } from "./broadcast-reactions";
import { PipBroadcastContent } from "./pip-broadcast-content";
import { PipCallContent } from "./pip-call-content";
import { PipWindowHeader } from "./pip-window-header";
import { VolumeControl } from "./volume-control";

type PipCombinedContentProps = {
	showCall: boolean;
	showBroadcast: boolean;
	onClose?: () => void;
	onGoBack?: () => void;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Unified PiP component handling multiple layout modes
export function PipCombinedContent({
	showCall,
	showBroadcast,
	onClose,
	onGoBack,
}: PipCombinedContentProps) {
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const room = useStore(uiStore, (state) => state.room);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const broadcastRoom = useStore(uiStore, (state) => state.broadcastRoom);
	const storeLayoutMode = useStore(
		uiStore,
		(state) => state.combinedLayoutMode
	);
	const layoutMode =
		storeLayoutMode === "spotlight-voice" ||
		storeLayoutMode === "spotlight-broadcast"
			? "tabs"
			: storeLayoutMode;
	const setLayoutMode = useStore(
		uiStore,
		(state) => state.setCombinedLayoutMode
	);

	const activeTab = useStore(uiStore, (state) => state.combinedActiveTab);
	const setActiveTab = useStore(uiStore, (state) => state.setCombinedActiveTab);
	const containerRef = useRef<HTMLDivElement>(null);
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
		null
	);

	useEffect(() => {
		setPortalContainer(containerRef.current);
	}, []);

	const layoutOptions = useMemo(
		() => [
			{
				value: "split-vertical" as const,
				label: "Split Vertical",
				icon: Columns2,
				group: "split",
			},
			{
				value: "split-horizontal" as const,
				label: "Split Horizontal",
				icon: Rows2,
				group: "split",
			},
			{ value: "tabs" as const, label: "Tabs", icon: Square, group: "single" },
		],
		[]
	);

	const callAvailable = activeChannel && room;
	const broadcastAvailable =
		broadcast && broadcastRoom && broadcast.status === "live";

	const hasCall = callAvailable && showCall;
	const hasBroadcast = broadcastAvailable && showBroadcast;

	if (hasCall && hasBroadcast) {
		const voicePanel = (
			<RoomContext.Provider value={room}>
				<PipCallContent hideHeader={layoutMode === "tabs"} />
			</RoomContext.Provider>
		);

		const broadcastPanel = (
			<RoomContext.Provider value={broadcastRoom}>
				<PipBroadcastContent hideHeader={layoutMode === "tabs"} />
			</RoomContext.Provider>
		);

		const layoutDropdown = (
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button size="icon" title="Change layout" variant="secondary">
						<LayoutDashboard className="size-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					align="end"
					className="w-48"
					container={portalContainer}
				>
					<DropdownMenuLabel>Layout</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{layoutOptions
						.filter((o) => o.group === "single")
						.map((option) => (
							<DropdownMenuItem
								key={option.value}
								onClick={() => setLayoutMode(option.value)}
							>
								<option.icon className="mr-2 size-4" />
								{option.label}
								{layoutMode === option.value && (
									<span className="ml-auto text-primary">✓</span>
								)}
							</DropdownMenuItem>
						))}
					<DropdownMenuSeparator />
					{layoutOptions
						.filter((o) => o.group === "split")
						.map((option) => (
							<DropdownMenuItem
								key={option.value}
								onClick={() => setLayoutMode(option.value)}
							>
								<option.icon className="mr-2 size-4" />
								{option.label}
								{layoutMode === option.value && (
									<span className="ml-auto text-primary">✓</span>
								)}
							</DropdownMenuItem>
						))}
				</DropdownMenuContent>
			</DropdownMenu>
		);

		if (layoutMode === "split-vertical" || layoutMode === "split-horizontal") {
			const direction =
				layoutMode === "split-vertical" ? "vertical" : "horizontal";
			return (
				<div
					className="relative flex h-screen w-screen flex-col bg-card"
					ref={containerRef}
				>
					<PipWindowHeader onClose={onClose} onGoBack={onGoBack} />
					<div className="relative flex-1">
						<div className="absolute top-2 right-2 z-10 flex gap-2">
							{layoutDropdown}
						</div>
						<ResizablePanelGroup direction={direction}>
							<ResizablePanel defaultSize={50} minSize={20}>
								{voicePanel}
							</ResizablePanel>
							<ResizableHandle withHandle />
							<ResizablePanel defaultSize={50} minSize={20}>
								{broadcastPanel}
							</ResizablePanel>
						</ResizablePanelGroup>
					</div>
				</div>
			);
		}

		if (layoutMode === "tabs") {
			return (
				<div
					className="flex h-screen w-screen flex-col bg-card"
					ref={containerRef}
				>
					<PipWindowHeader onClose={onClose} onGoBack={onGoBack} />
					<div className="flex shrink-0 items-center justify-between border-b px-3 py-1.5">
						<div className="flex min-w-0 items-center gap-1">
							<button
								className={`flex items-center gap-1 rounded-md px-2 py-1 font-medium text-xs transition-colors ${
									activeTab === "voice"
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-muted hover:text-foreground"
								}`}
								onClick={() => setActiveTab("voice")}
								type="button"
							>
								<Video className="size-3 shrink-0" />
								<span className="truncate">
									{activeChannel?.name ?? "Video"}
								</span>
							</button>
							<button
								className={`flex items-center gap-1 rounded-md px-2 py-1 font-medium text-xs transition-colors ${
									activeTab === "broadcast"
										? "bg-primary/10 text-primary"
										: "text-muted-foreground hover:bg-muted hover:text-foreground"
								}`}
								onClick={() => setActiveTab("broadcast")}
								type="button"
							>
								<Radio className="size-3 shrink-0" />
								Broadcast
							</button>
						</div>
						<div className="flex shrink-0 items-center gap-2">
							{activeTab === "broadcast" && (
								<>
									<RoomContext.Provider value={broadcastRoom}>
										<BroadcastReactions container={portalContainer} />
									</RoomContext.Provider>
									<VolumeControl container={portalContainer} />
								</>
							)}
							{layoutDropdown}
							{activeTab === "voice" && room && (
								<span className="rounded-md bg-secondary text-secondary-foreground text-xs">
									{room.remoteParticipants.size + 1} participant
									{room.remoteParticipants.size + 1 !== 1 ? "s" : ""}
								</span>
							)}
							{activeTab === "broadcast" && broadcastRoom && (
								<span className="rounded-md bg-secondary text-secondary-foreground text-xs">
									{broadcastRoom.remoteParticipants.size + 1} viewer
									{broadcastRoom.remoteParticipants.size + 1 !== 1 ? "s" : ""}
								</span>
							)}
						</div>
					</div>
					<div className="flex-1 overflow-hidden">
						{activeTab === "voice" ? voicePanel : broadcastPanel}
					</div>
				</div>
			);
		}
	}

	if (hasCall) {
		return (
			<div className="flex h-screen w-screen flex-col">
				<PipWindowHeader onClose={onClose} onGoBack={onGoBack} />
				<div className="flex-1 overflow-hidden">
					<RoomContext.Provider value={room}>
						<PipCallContent />
					</RoomContext.Provider>
				</div>
			</div>
		);
	}

	if (hasBroadcast) {
		return (
			<div className="flex h-screen w-screen flex-col">
				<PipWindowHeader onClose={onClose} onGoBack={onGoBack} />
				<div className="flex-1 overflow-hidden">
					<RoomContext.Provider value={broadcastRoom}>
						<PipBroadcastContent />
					</RoomContext.Provider>
				</div>
			</div>
		);
	}

	return null;
}
